import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  generateNonce,
  getAuthorizeUrl,
  getOrgDomain,
  setOrgDomain,
} from "@/lib/powtoon-oauth";
import {
  isAuthInflight,
  markAuthInflight,
  clearAuthInflight,
} from "@/lib/auth/postLoginDestination";
import { toast } from "sonner";
import { setSentryUser, clearSentryUser } from "@/lib/sentry";
import { setAnalyticsUser, clearAnalyticsUser, trackEvent } from "@/services/analytics";
import { ANALYTICS_CATEGORIES, ANALYTICS_ACTIONS } from "@/config/analytics-config";
import { setLoggerIdentity, clearLoggerIdentity, createLogger } from "@/lib/frontend-logger";

const logger = createLogger("auth.PowtoonAuthContext");

const POWTOON_CLIENT_ID = import.meta.env.VITE_POWTOON_CLIENT_ID as
  | string
  | undefined;
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const EXPIRY_BUFFER_MS = 2 * 60 * 1000;

export const TOKEN_KEY = "powtoon_access_token";
const REFRESH_KEY = "powtoon_refresh_token";
const EXPIRES_KEY = "powtoon_token_expires_at";
const USER_KEY = "powtoon_user";

interface PowtoonUser {
  powtoonUserId: number;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  permissions: { canView: boolean; canEdit: boolean };
}

interface InstallSessionPayload {
  access_token: string;
  refresh_token?: string | null;
  expires_in?: number | null;
  user: PowtoonUser;
}

interface PowtoonAuthContextType {
  user: PowtoonUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  accessToken: string | null;
  orgDomain: string | null;
  signInWithPowtoon: () => void;
  signOut: () => void;
  getValidToken: () => Promise<string | null>;
  installSession: (payload: InstallSessionPayload) => void;
}

const Ctx = createContext<PowtoonAuthContextType | undefined>(undefined);

function readUser(): PowtoonUser | null {
  if (typeof window === "undefined") return null;
  const raw = window.sessionStorage.getItem(USER_KEY);
  return raw ? JSON.parse(raw) : null;
}

export function PowtoonAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PowtoonUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [orgDomain, setOrgDomainState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshingRef = useRef<Promise<string | null> | null>(null);
  const autoLoginTriggeredRef = useRef(false);

  const isAuthenticated = !!accessToken && !!user;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const paramDomain = params.get("orgDomain");
    if (paramDomain) setOrgDomain(paramDomain);
    setOrgDomainState(getOrgDomain());
    const storedToken = window.sessionStorage.getItem(TOKEN_KEY);
    if (!storedToken && window.location.pathname !== "/process-authcode") {
      clearAuthInflight();
    }
    setAccessToken(storedToken);
    const storedUser = readUser();
    setUser(storedUser);
    if (storedUser) {
      setSentryUser(storedUser.powtoonUserId, storedUser.email);
      setAnalyticsUser(storedUser.powtoonUserId);
      setLoggerIdentity(storedUser.powtoonUserId, storedUser.displayName ?? undefined);
    }
    setIsLoading(false);
  }, []);

  const clearSession = useCallback(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.removeItem(TOKEN_KEY);
    window.sessionStorage.removeItem(REFRESH_KEY);
    window.sessionStorage.removeItem(EXPIRES_KEY);
    window.sessionStorage.removeItem(USER_KEY);
    setAccessToken(null);
    setUser(null);
    clearSentryUser();
    clearAnalyticsUser();
    clearLoggerIdentity();
    logger.info("auth signed out");
    trackEvent({ category: ANALYTICS_CATEGORIES.AUTH, action: ANALYTICS_ACTIONS.COMPLETED, ga4EventName: "auth_logout" });
  }, []);

  const signInWithPowtoon = useCallback(() => {
    if (!POWTOON_CLIENT_ID) {
      toast.error("VITE_POWTOON_CLIENT_ID is not configured.");
      return;
    }
    if (!getOrgDomain()) {
      toast.error("Missing orgDomain query parameter. Open this app via your Powtoon workspace link.");
      return;
    }
    const state = generateNonce();
    document.cookie = `oauth_state=${state}; path=/; max-age=600; SameSite=Lax`;
    markAuthInflight();
    logger.info("auth started", { orgDomain: getOrgDomain() });
    trackEvent({ category: ANALYTICS_CATEGORIES.AUTH, action: ANALYTICS_ACTIONS.STARTED, ga4EventName: "auth_started" });
    window.location.href = getAuthorizeUrl(POWTOON_CLIENT_ID, state);
  }, []);

  useEffect(() => {
    if (isLoading || autoLoginTriggeredRef.current) return;
    if (typeof window === "undefined") return;
    if (window.location.pathname === "/process-authcode") return;
    const hasOrgDomain = new URLSearchParams(window.location.search).has("orgDomain");
    const hasStoredToken = !!window.sessionStorage.getItem(TOKEN_KEY);
    if (hasOrgDomain && !accessToken && !user && !hasStoredToken && !isAuthInflight()) {
      autoLoginTriggeredRef.current = true;
      signInWithPowtoon();
    }
  }, [isLoading, accessToken, user, signInWithPowtoon]);

  const signOut = useCallback(() => clearSession(), [clearSession]);

  const refreshToken = useCallback(async (): Promise<string | null> => {
    const rt = window.sessionStorage.getItem(REFRESH_KEY);
    if (!rt) {
      clearSession();
      return null;
    }
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/powtoon-refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: rt }),
      });
      if (!res.ok) {
        logger.warn("token refresh failed", { status: res.status });
        clearSession();
        return null;
      }
      const data = await res.json();
      window.sessionStorage.setItem(TOKEN_KEY, data.access_token);
      setAccessToken(data.access_token);
      if (data.refresh_token)
        window.sessionStorage.setItem(REFRESH_KEY, data.refresh_token);
      if (data.expires_in)
        window.sessionStorage.setItem(
          EXPIRES_KEY,
          String(Date.now() + data.expires_in * 1000),
        );
      logger.info("token refreshed");
      return data.access_token;
    } catch (err) {
      logger.error("token refresh threw", { error: err instanceof Error ? err.message : String(err) });
      clearSession();
      return null;
    }
  }, [clearSession]);

  const getValidToken = useCallback(async (): Promise<string | null> => {
    if (typeof window === "undefined") return null;
    const tok = window.sessionStorage.getItem(TOKEN_KEY);
    if (!tok) return null;
    const expStr = window.sessionStorage.getItem(EXPIRES_KEY);
    if (expStr) {
      const exp = Number(expStr);
      if (Date.now() >= exp - EXPIRY_BUFFER_MS) {
        if (!refreshingRef.current) {
          refreshingRef.current = refreshToken().finally(() => {
            refreshingRef.current = null;
          });
        }
        return refreshingRef.current;
      }
    }
    return tok;
  }, [refreshToken]);

  const installSession = useCallback((payload: InstallSessionPayload) => {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(TOKEN_KEY, payload.access_token);
    if (payload.refresh_token)
      window.sessionStorage.setItem(REFRESH_KEY, payload.refresh_token);
    if (payload.expires_in)
      window.sessionStorage.setItem(
        EXPIRES_KEY,
        String(Date.now() + payload.expires_in * 1000),
      );
    window.sessionStorage.setItem(USER_KEY, JSON.stringify(payload.user));
    setAccessToken(payload.access_token);
    setUser(payload.user);
    setSentryUser(payload.user.powtoonUserId, payload.user.email);
    setAnalyticsUser(payload.user.powtoonUserId);
    setLoggerIdentity(payload.user.powtoonUserId, payload.user.displayName ?? undefined);
    clearAuthInflight();
    logger.info("auth installed session", { powtoonUserId: payload.user.powtoonUserId });
    trackEvent({
      category: ANALYTICS_CATEGORIES.AUTH,
      action: ANALYTICS_ACTIONS.SUCCESS,
      ga4EventName: "auth_success",
      extraData: { powtoon_user_id: payload.user.powtoonUserId },
    });
  }, []);

  return (
    <Ctx.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        accessToken,
        orgDomain,
        signInWithPowtoon,
        signOut,
        getValidToken,
        installSession,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function usePowtoonAuth(): PowtoonAuthContextType {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("usePowtoonAuth must be used within PowtoonAuthProvider");
  return ctx;
}
